import { Component, Inject, OnInit } from '@angular/core';
import {
    FormControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AS_FIRST_ELEMENT_OPTION_ID } from '@modeso/aumico-lib-templates-fe/constants/subtotal-form.constant';
import { SortingTemplateItemsHelper } from '@modeso/aumico-lib-templates-fe/helpers/sorting-mapper.helper';
import { ITemplatesSubtotalFormResult } from '@modeso/aumico-lib-templates-fe/interfaces/templates-subtotal-form-result.interface';
import { IDynamicKeyValue, INameId } from '@modeso/types__aumico-lib-core-be';
import { I18NextService } from 'angular-i18next';
import { Subject } from 'rxjs';
import { LANGUAGES } from '../../../../../../aumico-lib-core-fe/src/lib';
import { ITemplatesSubtotalFormData } from '../../../interfaces/templates-subtotal-form-data.interface';
import { TemplatesProvider } from '../../../provider/templates.provider';
import { BaseComponent } from '../../base.component';

@Component({
    selector: 'subtotal-form',
    templateUrl: 'subtotal-form.component.html',
    styleUrls: ['subtotal-form.component.scss'],
})
export class SubtotalFormComponent extends BaseComponent implements OnInit {
    public subtotalNameControl: UntypedFormControl;
    public subtotalTranslatedFormGroup: UntypedFormGroup;
    public scopeFormControl: UntypedFormControl;
    public positionFormControl: UntypedFormControl;

    subtotalNameExists: boolean;

    public savePressed = new Subject<string>();
    public languages: INameId[];

    private languageToText: IDynamicKeyValue = {};
    private currentSelectedLanguage: string;
    constructor(
        public override templatesProvider: TemplatesProvider,
        private readonly localizedTexts: I18NextService,
        public dialogRef: MatDialogRef<SubtotalFormComponent>,
        private formBuilder: UntypedFormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: ITemplatesSubtotalFormData,
    ) {
        super(templatesProvider);
        this.languages = data.languages.map((language) => ({ name: language, id: language.toLowerCase() }));
        this.currentSelectedLanguage = LANGUAGES.SWISS_GERMAN;
    }
    override ngOnInit(): void {
        super.ngOnInit();
        this.addAsFirstElementOptionInPositionOptions();

        this.initFromControls();

        if (this.data.savedSubtotal != null) {
            this.handleEditSubtotal();
        }
    }

    notEmptyValidator(control: UntypedFormControl): ValidationErrors | null {
        if (control.value?.toString().trim() === '') {
            return { required: true };
        }
        return null;
    }

    private initFromControls(): void {
        this.subtotalNameControl = new UntypedFormControl('', [Validators.required, this.notEmptyValidator]);
        this.scopeFormControl = new UntypedFormControl(null, Validators.required);
        this.positionFormControl = new UntypedFormControl(null, Validators.required);

        this.subtotalTranslatedFormGroup = this.formBuilder.group({
            language: new FormControl<INameId>({ name: 'de-CH', id: LANGUAGES.SWISS_GERMAN }, [Validators.required]),
            description: new FormControl<string>('', [Validators.required, this.notEmptyValidator]),
        });
    }

    private handleEditSubtotal(): void {
        this.subtotalNameControl.setValue(this.data.savedSubtotal.name);
        this.subtotalTranslatedFormGroup
            ?.get('description')
            .setValue(this.data.savedSubtotal.description[LANGUAGES.SWISS_GERMAN]);
        this.languageToText = { ...this.data.savedSubtotal.description };
        this.scopeFormControl.setValue(this.data.savedSubtotal.scope);
        this.positionFormControl.setValue(this.data.savedSubtotal.positionBeforeSubtotal);
    }

    public save(): void {
        const subtotalName: string = this.subtotalNameControl.value.toString().trim();
        const positionBeforeSubtotal: INameId = this.positionFormControl.value;

        this.savePressed.next(subtotalName);

        if (this.subtotalNameExists) {
            this.subtotalNameControl.setErrors({ subtotalNameExists: true });
            return;
        }

        const positionBeforeSubtotalIndex = this.data.allItemsInGroup.findIndex((item) => {
            return SortingTemplateItemsHelper.isTemplateGroup(item)
                ? item.groupRef.name === positionBeforeSubtotal.id
                : item.name === positionBeforeSubtotal.id;
        });

        const { description, language } = this.subtotalTranslatedFormGroup.value;
        this.languageToText[language.id] = description;

        const result: ITemplatesSubtotalFormResult = {
            name: subtotalName,
            description: this.languageToText,
            scope: this.scopeFormControl.value,
            positionBeforeSubtotal: positionBeforeSubtotal,
            positionBeforeSubtotalIndex: positionBeforeSubtotalIndex,
        };
        this.closeDialog(result);
    }

    private addAsFirstElementOptionInPositionOptions(): void {
        const asFirstElementOption: INameId = {
            id: AS_FIRST_ELEMENT_OPTION_ID,
            name: this.localizedTexts.t('aumico_templates_subtotal_select_position_as_first_element') as string,
        };
        this.data.positionOptions.unshift(asFirstElementOption);
    }

    public closeDialog(data?: { name: string; description: { [key: string]: string } }): void {
        if (typeof this.dialogRef === 'object' && !(Object.keys(this.dialogRef).length === 0)) {
            this.dialogRef.close(data);
        }
    }

    onLanguageSelectionChange() {
        const { description, language } = this.subtotalTranslatedFormGroup.value;

        if (this.currentSelectedLanguage != null && description != null) {
            this.languageToText[this.currentSelectedLanguage] = description;
        }
        this.currentSelectedLanguage = language.id;

        if (this.languageToText[this.currentSelectedLanguage] != null) {
            const savedTextValue = this.languageToText[this.currentSelectedLanguage];
            this.subtotalTranslatedFormGroup.get('description').setValue(savedTextValue);
        } else {
            this.subtotalTranslatedFormGroup.get('description').reset();
        }
    }
}
